/* eslint-disable @typescript-eslint/no-explicit-any */
import { Modal, ModalBody, ModalContent, ModalFooter } from '@heroui/react';
import { FaUserTie } from 'react-icons/fa6';
import { MdCheckCircle, MdThumbDown } from 'react-icons/md';
import { getSiblingEffectiveState } from '../../../../utils/quoteFlowRequests';
import CustomButton from '../../CustomButton';
import { Job } from '../../../../utils/type';
import { useTranslation } from 'react-i18next';
import { formatCurrency } from '../../../../utils/currency';
import React from 'react';

export type AcceptDeclineAction = 'accept' | 'decline';

export interface BroadcastAcceptDeclineModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** All jobs in the broadcast batch that have quotes */
  quotedJobs: Job[];
  /** 'accept' or 'decline' */
  action: AcceptDeclineAction;
  /** Selected job/provider for the action */
  selectedJob: Job | null;
  onSelectedJobChange: (job: Job | null) => void;
  /** Called when user confirms the action */
  onConfirm: (job: Job) => void | Promise<void>;
  isLoading?: boolean;
}

const BroadcastAcceptDeclineModal = ({
  isOpen,
  onClose,
  quotedJobs,
  action,
  selectedJob,
  onSelectedJobChange,
  onConfirm,
  isLoading = false,
}: BroadcastAcceptDeclineModalProps) => {
  const { t } = useTranslation();

  // Filter to only show jobs with live quotes
  const availableJobs = quotedJobs.filter(
    (j) => getSiblingEffectiveState(j) === 'quoted-live'
  );

  // Single provider case: auto-select and skip to confirmation
  const isSingleProvider = availableJobs.length === 1;

  // Auto-select single provider on mount or when jobs change
  React.useEffect(() => {
    if (isSingleProvider && availableJobs.length > 0 && !selectedJob) {
      onSelectedJobChange(availableJobs[0]);
    }
  }, [isSingleProvider, availableJobs, selectedJob, onSelectedJobChange]);

  const isAccept = action === 'accept';
  const icon = isAccept ? (
    <MdCheckCircle size={32} />
  ) : (
    <MdThumbDown size={32} />
  );
  const iconBg = isAccept ? 'bg-primary/20' : 'bg-danger/20';
  const iconColor = isAccept ? 'text-primary' : 'text-danger';

  const getTitle = () => {
    if (isSingleProvider && selectedJob) {
      return isAccept
        ? t('jobs.confirm_accept_title', 'Accept this quote?')
        : t('jobs.confirm_decline_title', 'Decline this quote?');
    }
    return isAccept
      ? t('jobs.select_quote_to_accept', 'Select Quote to Accept')
      : t('jobs.select_quote_to_decline', 'Select Quote to Decline');
  };

  const getDescription = () => {
    if (isSingleProvider && selectedJob) {
      return isAccept
        ? t(
            'jobs.confirm_accept_desc',
            'Accepting locks in the quoted price. The provider will schedule the work.'
          )
        : t(
            'jobs.confirm_decline_desc',
            'Declining closes this request. You can request a new quote if you change your mind.'
          );
    }
    return isAccept
      ? t(
          'jobs.select_quote_to_accept_desc',
          'Select which provider quote you want to accept. Other providers will be notified.'
        )
      : t(
          'jobs.select_quote_to_decline_desc',
          'Select which provider quote you want to decline.'
        );
  };

  const getConfirmButtonLabel = () => {
    if (isAccept) {
      return selectedJob
        ? t('jobs.accept_quote', 'Accept Quote')
        : t('jobs.select_quote_to_accept', 'Select Quote to Accept');
    } else {
      return selectedJob
        ? t('jobs.decline_quote', 'Decline Quote')
        : t('jobs.select_quote_to_decline', 'Select Quote to Decline');
    }
  };

  const getConfirmButtonColor = () => {
    return isAccept ? 'primary' : 'danger';
  };

  const handleConfirm = async () => {
    if (!selectedJob) return;
    await onConfirm(selectedJob);
  };

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={(open) => !open && onClose()}
      hideCloseButton
      size="2xl"
      scrollBehavior="inside"
    >
      <ModalContent className="max-h-[90vh]">
        <ModalBody className="overflow-y-auto px-6 py-4">
          <div className="flex flex-col items-center space-y-4 py-2">
            {/* Icon */}
            <div
              className={`p-3 rounded-full ${iconBg} flex items-center justify-center`}
            >
              <div className={iconColor}>{icon}</div>
            </div>

            {/* Title */}
            <h3 className="text-xl font-semibold text-gray-900 text-center">
              {getTitle()}
            </h3>

            {/* Description */}
            <p className="text-sm text-gray-600 text-center">
              {getDescription()}
            </p>

            {/* Provider Selection Cards (only if multiple providers) */}
            {!isSingleProvider && (
              <div className="w-full space-y-3 mt-4">
                {availableJobs.map((job) => {
                  const providerName =
                    job.businessName ||
                    t('jobs.unknown_provider', 'Unknown Provider');
                  const serviceTitle =
                    job.serviceTitle ||
                    job.serviceName ||
                    t('jobs.service', 'Service');
                  const quotedPrice = job.quotedPrice || job.total || 0;
                  const providerAvatar =
                    'avatar' in job
                      ? (job as { avatar?: string }).avatar
                      : undefined;

                  const isSelected = selectedJob?.jobId === job.jobId;

                  return (
                    <div
                      key={job.jobId}
                      onClick={() => onSelectedJobChange(job)}
                      className={`p-3 rounded-xl border-2 cursor-pointer transition-all ${
                        isSelected
                          ? isAccept
                            ? 'border-primary bg-primary/5'
                            : 'border-danger bg-danger/5'
                          : 'border-slate-200 bg-white hover:border-slate-300'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        {/* Radio button */}
                        <div
                          className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 ${
                            isSelected
                              ? isAccept
                                ? 'border-primary bg-primary'
                                : 'border-danger bg-danger'
                              : 'border-slate-300'
                          }`}
                        >
                          {isSelected && (
                            <div className="w-2 h-2 rounded-full bg-white" />
                          )}
                        </div>

                        {/* Avatar */}
                        {providerAvatar ? (
                          <img
                            src={providerAvatar}
                            alt={providerName}
                            className="w-10 h-10 rounded-lg object-cover"
                          />
                        ) : (
                          <div className="w-10 h-10 rounded-lg bg-slate-200 flex items-center justify-center">
                            <FaUserTie className="text-slate-500" size={16} />
                          </div>
                        )}

                        {/* Provider Info */}
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-sm text-slate-900 truncate">
                            {providerName}
                          </p>
                          <p className="text-xs text-slate-500 truncate mt-0.5">
                            {serviceTitle}
                          </p>
                        </div>

                        {/* Price */}
                        <div className="text-right shrink-0">
                          <p className="font-bold text-sm text-slate-900">
                            {formatCurrency(quotedPrice)}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Single Provider Confirmation Details */}
            {isSingleProvider && selectedJob && (
              <div className="w-full p-4 rounded-xl bg-slate-50 border border-slate-200 space-y-3 mt-4">
                <div className="flex items-start gap-3">
                  {(selectedJob as any).avatar ? (
                    <img
                      src={(selectedJob as any).avatar}
                      alt={selectedJob.businessName ?? undefined}
                      className="w-12 h-12 rounded-lg object-cover"
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-lg bg-slate-300 flex items-center justify-center">
                      <FaUserTie className="text-slate-600" size={18} />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-slate-900">
                      {selectedJob.businessName ||
                        t('jobs.unknown_provider', 'Unknown Provider')}
                    </p>
                    <p className="text-sm text-slate-600 mt-1">
                      {selectedJob.serviceTitle ||
                        selectedJob.serviceName ||
                        t('jobs.service', 'Service')}
                    </p>
                  </div>
                </div>
                <div className="border-t border-slate-200 pt-3">
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-400">
                    {t('jobs.quoted_price', 'Quoted Price')}
                  </p>
                  <p className="text-2xl font-bold text-slate-900 mt-1">
                    {formatCurrency(
                      selectedJob.quotedPrice || selectedJob.total || 0
                    )}
                  </p>
                </div>
              </div>
            )}
          </div>
        </ModalBody>

        <ModalFooter className="flex justify-center items-center gap-3 p-4 shrink-0 border-t border-slate-100">
          <CustomButton
            label={t('jobs.cancel', 'Cancel')}
            color="default"
            variant="solid"
            className="w-full text-xs"
            size="sm"
            onPress={onClose}
            isDisabled={isLoading}
          />

          <CustomButton
            label={getConfirmButtonLabel()}
            color={getConfirmButtonColor()}
            className="w-full text-xs"
            variant="solid"
            isDisabled={!selectedJob || isLoading}
            isLoading={isLoading}
            onPress={handleConfirm}
          />
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default BroadcastAcceptDeclineModal;
